import type { BrowserRouter } from '../../app/router';
import { loadSciBooks } from '../../data/sci';
import { ScriptureSelectionSchema } from '../../data/schema';
import { createVerseHistory } from './VerseHistory';
import type { StudyStore } from '../../services/studyStore';
import {
  createShareButton,
  setShareMetadata,
} from '../../services/shareMetadata';
import {
  compactVerseSelection,
  officialChurchScriptureUrl,
  parseVerseSelection,
  sciScriptureUrl,
} from './sciLinks';

function routeError(message: string): HTMLElement {
  const state = document.createElement('div');
  state.className = 'state-panel state-panel--error page-width';
  state.dataset.route = 'scripture';
  state.setAttribute('role', 'alert');
  const title = document.createElement('h1');
  title.textContent = 'This scripture history could not be opened';
  const copy = document.createElement('p');
  copy.textContent = message;
  state.append(title, copy);
  return state;
}

export function createVerseRoute(
  bookId: string,
  chapterText: string,
  selectionText: string,
  router: BrowserRouter,
  studyStore?: StudyStore,
): HTMLElement {
  const page = document.createElement('div');
  page.className = 'verse-route page-width';
  page.dataset.route = 'scripture';
  const loading = document.createElement('div');
  loading.className = 'state-panel';
  loading.setAttribute('role', 'status');
  loading.textContent = 'Opening verse history…';
  page.append(loading);

  const chapter = Number(chapterText);
  let verses: number[];
  try {
    if (!Number.isSafeInteger(chapter) || chapter <= 0) {
      throw new Error('Chapter must be a positive number.');
    }
    verses = parseVerseSelection(selectionText);
  } catch {
    return routeError('The chapter or verse selection is not valid.');
  }

  void loadSciBooks()
    .then((index) => {
      const book = index.books.find((candidate) => candidate.bookId === bookId);
      if (!book) {
        page.replaceWith(routeError('The requested SCI book ID is not available.'));
        return;
      }
      const canonicalSelection = compactVerseSelection(verses);
      const selection = ScriptureSelectionSchema.parse({
        bookId,
        bookName: book.name,
        chapter,
        verses,
        displayLabel: `${book.name} ${String(chapter)}:${canonicalSelection}`,
        officialChurchUrl: officialChurchScriptureUrl(bookId, chapter, verses),
        sciUrl: sciScriptureUrl(bookId, chapter, verses),
      });
      const header = document.createElement('header');
      header.className = 'verse-route__header';
      const eyebrow = document.createElement('p');
      eyebrow.className = 'eyebrow eyebrow--gold';
      eyebrow.textContent = 'Verse history';
      const title = document.createElement('h1');
      title.textContent = selection.displayLabel;
      const body = document.createElement('p');
      body.textContent =
        'Explore every available local citation occurrence, grouped by source and decade.';
      const pathname = `/scripture/${bookId}/${String(chapter)}/${canonicalSelection}`;
      header.append(
        eyebrow,
        title,
        body,
        createShareButton('Copy scripture link', pathname, selection.displayLabel),
      );
      page.replaceChildren(
        header,
        createVerseHistory({ selection, router }),
      );
      setShareMetadata({
        title: `${selection.displayLabel} citation history`,
        description: `Citation history and conference connections for ${selection.displayLabel}.`,
        pathname,
      });
      void studyStore
        ?.saveExploredScripture(bookId, chapter, verses)
        .catch(() => undefined);
    })
    .catch(() => {
      page.replaceChildren(routeError('Scripture metadata could not be loaded.'));
    });
  return page;
}
